import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import {
  AiAutopilotAddedToBilling,
  ConversationMode,
  ConversationPlatform,
  ConversationStatus,
  InputStepType,
} from "../conversation";
import { ConversationInsightsSchema } from "../conversation-insights";
import { ConversationTagSource } from "../conversation-tags/conversation-tag-record";
import { PillTheme } from "../pill-theme";
import { ConversationMessageDbParser } from "./conversation-message-db-parser";
import { DateSchema } from "./shared-schemas";

export const CURRENT_CONVERSATION_VERSION = "v1";

export const EmailAddressInfoSchema = z.object({
  email: z.string().nullish(),
  name: z.string().nullish(),
});

/**
 * Base schema for analytics interaction events
 * Contains common fields like productId, variantId, and timestamp
 */
export const BaseAnalyticsInteractionDbParser = z.object({
  productId: z.string(),
  productName: z.string().nullish(),
  imgSrc: z.string().nullish(),
  variantId: z.string().nullish(),
  timestamp: DateSchema.nullish(),
});

/**
 * Schema for product interaction events (e.g., pdpInteractions)
 */
export const ProductInteractionDbParser =
  BaseAnalyticsInteractionDbParser.extend({
    action: z.string().nullish(),
    timestamp: DateSchema.nullish(),
  });

/**
 * Schema for cart interaction events (e.g., productsAddedToCart)
 */
export const CartInteractionDbParser = BaseAnalyticsInteractionDbParser.extend({
  quantity: z.number(),
});

/**
 * Schema for back in stock subscription events
 */
export const BackInStockSubscriptionDbParser = BaseAnalyticsInteractionDbParser;

/**
 * Schema for suggested product events
 */
export const SuggestedProductDbParser = z.object({
  productId: z.string(),
  timestamp: DateSchema.nullish(),
});

export const ConversationAnalyticsDbParser = z.object({
  pdpInteractions: z.array(ProductInteractionDbParser).nullish(),
  suggestedProducts: z.array(SuggestedProductDbParser).nullish(),
  productsAddedToCart: z.array(CartInteractionDbParser).nullish(),
  backInStockSubscriptions: z.array(BackInStockSubscriptionDbParser).nullish(),
});

export type ConversationAnalytics = z.infer<
  typeof ConversationAnalyticsDbParser
>;

export const AIInformationDbParser = z.object({
  version: z.number().default(1).nullish(),
  categories: z.array(z.string()).nullish(),
  analytics: ConversationAnalyticsDbParser.nullish(),
  orders: z.array(zExt.objectId()).nullish(),
  excludeFromEmbedding: z.boolean().default(false).nullish(),
  hasTrainedSuggestedKnowledge: z.boolean().nullish(),
  shopifyOrders: z.array(z.string()).nullish(),
  llmThreadId: zExt.objectId().nullish(),
  supportAgentTreatmentId: z.string().nullish(),
});

export type AIInformation = z.infer<typeof AIInformationDbParser>;

export enum SMSKind {
  ATTENTIVE = "attentive",
  POSTSCRIPT = "postscript",
  NUMBER = "number",
}

export const SMSInfoDbParser = z.object({
  kind: createEnum(SMSKind, { enumName: "SMSKind" }).nullish(),
  phoneNumber: z.string().nullish(),
  merchantPhoneNumber: z.string().nullish(),
});

export type SMSInfo = z.infer<typeof SMSInfoDbParser>;

export enum EmailKind {
  GMAIL = "gmail",
  OUTLOOK = "outlook",
  CUSTOM = "custom",
}

export const EmailInfoDbParser = z.object({
  kind: createEnum(EmailKind, { enumName: "EmailKind" }).nullish(),
  email: z.string().nullish(),
  gmailThreadId: z.string().nullish(),
  outlookConversationId: z.string().nullish(),
});

export type EmailInfo = z.infer<typeof EmailInfoDbParser>;

export const CustomerInfoDbParser = z.object({
  customer: zExt.objectId().nullish(),
  email: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  name: z.string().nullish(),
  phoneNumber: z.string().nullish(),
  shopifyCustomerId: z.string().nullish(),
});

export type CustomerInfo = z.infer<typeof CustomerInfoDbParser>;

export const ConversationTagDbParser = z.object({
  tagId: zExt.objectId(),
  name: z.string(),
  pillTheme: createEnum(PillTheme, { enumName: "PillTheme" }).nullish(),
  source: createEnum(ConversationTagSource, {
    enumName: "ConversationTagSource",
  }).nullish(),
  createdAt: DateSchema.nullish(),
  updatedAt: DateSchema.nullish(),
});

export const FullConversationTagSchema = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  pillTheme: createEnum(PillTheme, { enumName: "PillTheme" }),
  teamId: zExt.objectId(),
  tagGroupId: zExt.objectId().optional(),
  source: createEnum(ConversationTagSource, {
    enumName: "ConversationTagSource",
  }).nullish(),
});

export const ConversationTagSchema = FullConversationTagSchema.omit({
  _id: true,
  teamId: true,
  tagGroupId: true,
});

export const ConversationFilesSchema = z.object({
  url: z.string(),
  description: z.string().nullish(),
  mimeType: z.string().nullish(),
  originalContentId: z.string().nullish(),
  createdAt: DateSchema.nullish(),
  updatedAt: DateSchema.nullish(),
});

export type FullConversationTagSchemaType = z.infer<
  typeof FullConversationTagSchema
>;

export const AuditLogDbParser = z.object({
  /**
   * The id of the last message in the conversation. Used to mark the context
   * the system had when it executed.
   */
  lastMessageId: z.string(),
  guardrailOutput: z.object({
    /**
     * The decision made by the guardrails.
     */
    decision: z.string(),
    /**
     * The reason for the decision.
     */
    decisionReason: z.string(),
    /**
     * The proposed message plan being evaluated by the guardrails.
     */
    proposedMessagePlan: z.string(),
  }),
  knowledgeUsed: z.object({
    /**
     * The ids of the knowledge the system was aware of when generating its
     * response.
     */
    knowledgeIds: z.array(zExt.objectId()),
  }),
});

export const instagramCommentThreadSchema = z.object({
  topLevelCommentId: z.string(),
  mediaId: z.string(),
  mediaPreviewUrl: z.string().nullish(),
  permalink: z.string().nullish(),
});

export const facebookCommentThreadSchema = z.object({
  topLevelCommentId: z.string(),
  postId: z.string(),
  permalink: z.string(),
  mediaPreviewUrl: z.string().nullish(),
});

export type ConversationTag = z.infer<typeof ConversationTagDbParser>;

const ConversationSchemaDbParser_ = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  mode: createEnum(ConversationMode, {
    enumName: "ConversationMode",
  }).nullish(),
  platform: createEnum(ConversationPlatform, {
    enumName: "ConversationPlatform",
  }),
  originalPlatform: createEnum(ConversationPlatform, {
    enumName: "ConversationPlatform",
  }).nullish(),
  email: EmailInfoDbParser.nullish(),
  sms: SMSInfoDbParser.nullish(),
  status: createEnum(ConversationStatus, { enumName: "ConversationStatus" }),
  subject: z.string().nullish(),
  lastResponseAt: DateSchema,
  lastCustomerResponseAt: DateSchema.nullish(),
  lastMerchantResponseAt: DateSchema.nullish(),
  lastKnownStepId: z.number().nullable().nullish(),
  inputStepType: createEnum(InputStepType, {
    enumName: "InputStepType",
  }).nullish(),
  fourDigitCodeId: z.string().nullish(),
  createdAt: DateSchema,
  updatedAt: DateSchema,
  closedAt: DateSchema.nullish(),
  archived: z.boolean().nullish(),
  archivedAt: DateSchema.nullish(),
  assignee: zExt.objectId().nullish(),
  read: z.boolean().nullish(),
  customer: CustomerInfoDbParser.nullish(),
  messages: z.array(ConversationMessageDbParser),
  auditLog: z.array(AuditLogDbParser).nullish(),
  aiInformation: AIInformationDbParser.nullish(),
  tags: z.array(ConversationTagSchema).nullish(),
  files: z.array(ConversationFilesSchema).nullish(),
  ccEmails: z.array(z.string()).nullish(),
  bccEmails: z.array(z.string()).nullish(),
  toEmails: z.array(EmailAddressInfoSchema).nullish(),
  tagIds: z.array(ConversationTagDbParser).nullish(),
  ticketNumber: z.number().nullish(),
  instagramCommentThread: instagramCommentThreadSchema.nullish(),
  facebookCommentThread: facebookCommentThreadSchema.nullish(),
  metaPageName: z.string().nullish(),
  metaIntegrationId: zExt.objectId().nullish(),
  snoozedUntil: DateSchema.nullish(),
  expiredAt: DateSchema.nullish(),
  carts: z.array(z.string()).nullish(),
  embedding: z.array(z.number()).nullish(),
  offeringConvertToEmail: z.boolean().nullish(),
  currentAiResponse: z.string().nullish(),
  emailMessageId: z.string().nullish(),
  partOfConciergeMigration: z.boolean().nullish(),
  conciergeMigratedConversationId: zExt.objectId().nullish(),
  aiBillable: z.boolean().nullish(),
  aiBillableCheckedAt: DateSchema.nullish(),
  baseBillable: z.boolean().optional(),
  baseBillableCheckedAt: DateSchema.optional(),
  aiAutopilotAddedToBilling: createEnum(AiAutopilotAddedToBilling, {
    enumName: "AiAutopilotAddedToBilling",
  }).nullish(),
  aiResolutionPriceInCents: z.number().nullish(),
  cameFromContactForm: z.boolean().nullish(),
  tiktokConversationId: z.string().nullish(),
  categories: z.array(z.string()).nullish(),
  needsCategoryUpdate: z.boolean().nullish(),
  conversationInsights: ConversationInsightsSchema.nullish(),
});
export const ConversationSchemaDbParser: z.ZodType<Conversation> =
  ConversationSchemaDbParser_;
export interface Conversation
  extends z.infer<typeof ConversationSchemaDbParser_> {}
